import { dateToYMDformat } from 'utils/date'

export type Step = 'init' | 'payment' | 'payNow' | 'finish'
export type Action = 'default' | 'calendar' | 'camera' | 'summary' | 'refund' | 'outsourcePhoto'

type TerminationTermsLike = Partial<Pick<warehouse.WarehouseTerminationTerms, 'minDate' | 'canEndBeforeMinDate'>>

/** 'YYYY-MM-DD' -> Date o lokalnej polnocy (new Date('YYYY-MM-DD') parsuje jako UTC) */
export const ymdToLocalDate = (ymd: string) => {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** czy godzina wymeldowania (HH:MM[:SS]) na dzis juz minela */
export const hasCheckoutTimePassed = (checkoutTime?: string, now = new Date()) => {
  if (!checkoutTime) return false
  const [h, m = 0] = checkoutTime.split(':').map(Number)
  if (Number.isNaN(h)) return false
  const checkout = new Date(now)
  checkout.setHours(h, Number.isNaN(m) ? 0 : m, 0, 0)
  return now >= checkout
}

/**
 * Zakres dat zakonczenia umowy wg warunkow wypowiedzenia:
 * - floor: dzis, jesli znamy godzine wymeldowania i jeszcze nie minela; w przeciwnym razie jutro
 *   (godzina przychodzi dopiero z wyceny, wiec do tego czasu bezpiecznie zakladamy jutro)
 * - defaultDate: minDate z warunkow, ale nie wczesniej niz jutro
 * - minDate (kalendarz): floor, jesli canEndBeforeMinDate; w przeciwnym razie max(minDate, floor)
 * Daty w formacie YYYY-MM-DD - porownanie leksykograficzne jest poprawne.
 */
export const getTerminationDateLimits = (terms?: TerminationTermsLike, checkoutTime?: string) => {
  const now = new Date()
  const today = dateToYMDformat(now)
  const tomorrow = dateToYMDformat(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1))
  const floor = checkoutTime && !hasCheckoutTimePassed(checkoutTime, now) ? today : tomorrow
  const termsMinDate = terms?.minDate ? terms.minDate.slice(0, 10) : undefined
  const max = (a: string, b?: string) => (b && b > a ? b : a)
  return {
    /** najwczesniejsza data niezaleznie od warunkow (dzis/jutro) - wczesniej = zakonczenie wstecz */
    floor,
    termsMinDate,
    defaultDate: max(tomorrow, termsMinDate),
    minDate: terms?.canEndBeforeMinDate ? floor : max(floor, termsMinDate),
  }
}

/** czy wybrana data jest przed minDate z warunkow (mozliwa dodatkowa oplata) */
export const isBeforeTermsMinDate = (date?: string, terms?: TerminationTermsLike) => {
  const { termsMinDate } = getTerminationDateLimits(terms)
  return !!(terms?.canEndBeforeMinDate && date && termsMinDate && date.slice(0, 10) < termsMinDate)
}

export const getAmount = (selectedIndex: number, tiles?: payments.WarehouseRentalScheduleTile[]) => {
  let amount = 0
  if (tiles) {
    for (let i = 0; i < tiles.length; i++) {
      amount += Number(tiles[i].grossPrice)
      if (i === selectedIndex) break
    }
  }
  return amount
}

export const getTotalDeposits = (selectedData: payments.SelectedData, warehouse?: payments.CancelRentalResponseWarehouse) => {
  let deposit = 0
  if (warehouse?.itemsToReturn) {
    for (const item of warehouse.itemsToReturn) {
      if (selectedData.itemsToReturn?.[item.id]) {
        deposit += Number(item.deposit)
      }
    }
  }
  return deposit
}
/** pozycja przekazywana do components/Payments (getInvoices) */
export interface PaymentInvoice {
  invoiceId: string
  amount: string
  object?: string
}

/** nieoplacone faktury umowy z wyceny (zamiast globalnego /balance) */
export const getOverdueInvoices = (quote?: payments.Quote) =>
  (quote?.overdueInvoices ?? []).filter(invoice => !invoice.isPaid && Number(invoice.remainingAmount) > 0)

export const sumAmounts = (values: (string | number | undefined | null)[]) =>
  Math.round(values.reduce<number>((sum, value) => sum + (Number(value) || 0), 0) * 100) / 100

export const overdueToPaymentInvoices = (invoices: payments.QuoteOverdueInvoice[]): PaymentInvoice[] =>
  invoices.map(({ id, remainingAmount, object }) => ({ invoiceId: id, amount: remainingAmount, object }))

/** 'HH:MM[:SS]' -> 'HH:MM' */
export const formatCheckoutTime = (time?: string) => time ? time.slice(0, 5) : undefined

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY' (bez przesuniec strefy czasowej) */
export const formatYMD = (ymd?: string | null) => {
  if (!ymd) return ''
  const [y, m, d] = ymd.slice(0, 10).split('-')
  return `${d}.${m}.${y}`
}

/** podstawia {klucz} w tekscie tlumaczenia */
export const fillTemplate = (text: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((result, [key, value]) => result.split(`{${key}}`).join(String(value)), text)

/** numer konta do wyswietlenia: 3 pierwsze i 2 ostatnie cyfry, grupy jak w NRB (2 + 4x6) */
export const maskAccountNumber = (account: string) => {
  const chars = account.replace(/\s/g, '').replace(/^[A-Z]{2}/i, '').split('')
  const masked = chars.map((char, idx) => idx < 3 || idx >= chars.length - 2 ? char : '*').join('')
  return [masked.slice(0, 2), ...(masked.slice(2).match(/.{1,4}/g) || [])].filter(Boolean).join(' ')
}
